/** Month view — a 6×7 calendar overview. Each day shows up to three shift chips
 *  ("Name HH:MM"); click a chip to edit, or click the day to drill into its Day
 *  view. */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../components/ui/cn.js';
import { Chip } from '../../../components/ui/StatusPill.js';
import { type ShiftRow, type StaffRow } from '../hooks/useStaff.js';

import { monthMatrix, roleTone, sameDay, toTimeInput, weekDays } from './scheduleUtils.js';

const MAX_CHIPS = 3;

export function MonthView({
  anchor,
  staff,
  shifts,
  onPickDay,
  onEdit,
}: {
  anchor: Date;
  staff: StaffRow[];
  shifts: ShiftRow[];
  onPickDay: (date: Date) => void;
  onEdit: (shift: ShiftRow) => void;
}): ReactNode {
  const { t } = useTranslation();
  const weeks = monthMatrix(anchor);
  const headerDays = weeks[0] ?? weekDays(anchor);
  const today = new Date();
  const month = anchor.getMonth();
  const staffById = new Map(staff.map((s) => [s.id, s]));

  function shiftsForDay(day: Date): ShiftRow[] {
    return shifts
      .filter((sh) => sh.scheduled_start != null && sameDay(new Date(sh.scheduled_start), day))
      .sort((a, b) => (a.scheduled_start! < b.scheduled_start! ? -1 : 1));
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-border bg-surface-2 text-xs font-semibold uppercase tracking-wide text-muted">
        {headerDays.map((d) => (
          <div key={d.toISOString()} className="px-2 py-2 text-center">
            {d.toLocaleDateString(undefined, { weekday: 'short' })}
          </div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((day) => {
            const dayShifts = shiftsForDay(day);
            const inMonth = day.getMonth() === month;
            const isToday = sameDay(day, today);
            return (
              <div
                key={day.toISOString()}
                role="button"
                tabIndex={0}
                onClick={() => onPickDay(day)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onPickDay(day);
                }}
                className={cn(
                  'min-h-[104px] cursor-pointer border-b border-l border-border p-1.5 transition-colors first:border-l-0 hover:bg-surface-2',
                  !inMonth && 'bg-surface-2/40',
                )}
              >
                <div
                  className={cn(
                    'mb-1 text-right text-xs font-semibold',
                    inMonth ? 'text-fg' : 'text-faint',
                    isToday &&
                      'ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-black',
                  )}
                >
                  {day.getDate()}
                </div>
                <div className="grid gap-0.5">
                  {dayShifts.slice(0, MAX_CHIPS).map((sh) => {
                    const s = staffById.get(sh.staff_id);
                    return (
                      <button
                        key={sh.id}
                        data-testid={`shift-${sh.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(sh);
                        }}
                        className="block w-full"
                      >
                        <Chip
                          tone={roleTone(s?.app_role)}
                          className="w-full justify-start truncate text-[11px]"
                        >
                          <span className="truncate">
                            {s?.first_name ?? '—'} {toTimeInput(sh.scheduled_start!)}
                          </span>
                        </Chip>
                      </button>
                    );
                  })}
                  {dayShifts.length > MAX_CHIPS ? (
                    <span className="px-1 text-[11px] text-muted">
                      {t('staff.schedule.more', { count: dayShifts.length - MAX_CHIPS })}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
